import { Customer, getNumericPhoneNumber } from "shared";
import {
  ChangeEvent,
  FocusEvent,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";

import {
  areCustomersTheSame,
  navigateToSettings,
  phoneRegExp,
  saveCustomerInfo,
} from "../../utils";
import { useScrollToTop } from "../../../../hooks/useScrollToTop";
import {
  useIsMfa,
  useIsStateFarmPartitioned,
  useUrls,
} from "../../../../app/Providers/AppContext";
import { UpdateStatus, ValidationErrors } from "../../ContactInfo.types";
import { EditPageTemplate } from "../../EditPageTemplate";
import { mt } from "../../../../utils/tracking";
import { EditPhoneNumberForm } from "./form";
import { useDialog } from "../../../Dialog";
import { DialogButtonGroupV2, DialogV2 } from "../../../DialogV2";
import { unstable__Button as Button } from "@hagerty/react-components";
import { ContactInfoSkeleton } from "../../components";
import { CustomerPhone, CustomerPhoneKey } from "./EditPhoneNumber.types";

export type EditPhoneNumberPageProps = {
  initialCustomer: Customer;
};

/**
 * Edit the phone number fields in contact information
 */
export const EditPhoneNumberPage = ({
  initialCustomer,
}: EditPhoneNumberPageProps) => {
  const isStateFarmPartitioned = useIsStateFarmPartitioned();

  // State farm can not edit phone
  if (isStateFarmPartitioned) {
    navigateToSettings();
    return <ContactInfoSkeleton testId="edit-phone" rows={1} />;
  }

  useScrollToTop();

  return <EditPhoneNumberPageWrapped initialCustomer={initialCustomer} />;
};

const EditPhoneNumberPageWrapped = ({
  initialCustomer,
}: EditPhoneNumberPageProps) => {
  const isMfa = useIsMfa();
  const mfaDialog = useDialog();
  const urls = useUrls();
  const [status, setStatus] = useState<UpdateStatus>("ok");
  const [disableDialogButtons, setDisableDialogButtons] = useState(false);
  const [customerPhone, setCustomerPhone] = useState<CustomerPhone>({
    phone: initialCustomer.phone,
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [changedFields, setChangedFields] = useState(
    new Set<CustomerPhoneKey>()
  );

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  useEffect(() => {
    mt.trackView();
    setValidationErrors(getValidationErrors(customerPhone));
  }, []);

  return (
    <>
      <EditPageTemplate
        heading="Change your phone number"
        unsavedChangesDialogHeading="Save your phone number changes?"
        pending={status == "pending"}
        isFormDirty={checkIfDirty()}
        hasValidationErrors={hasValidationErrors}
        showMutateError={status === "mutateError"}
        onClickSave={onClickSave}
      >
        <EditPhoneNumberForm
          customerPhone={customerPhone}
          changedFields={changedFields}
          validationErrors={validationErrors}
          pending={status == "pending"}
          onBlur={onBlur}
          onChange={onChange}
        />
      </EditPageTemplate>

      {mfaDialog.isOpen && (
        <DialogV2
          heading="Phone number successfully changed."
          message="If you chose to receive two-step verification by phone, you will also need to update that in order to receive verification on this number."
          delayPopup
          onDismiss={() => false}
        >
          <DialogButtonGroupV2>
            <Button
              onClick={onClickMfaDialogNoThanks}
              variant="outline"
              size="small"
              disabled={disableDialogButtons}
            >
              No thanks
            </Button>
            <Button
              autoFocus
              as="a"
              target="_blank"
              href={urls.account.manageMfa(initialCustomer.country)}
              onClick={onClickMfaDialogUpdate}
              variant="solid"
              size="small"
            >
              Update verification
            </Button>
          </DialogButtonGroupV2>
        </DialogV2>
      )}
    </>
  );

  function onClickMfaDialogNoThanks() {
    setDisableDialogButtons(true);
    navigateToSettings(true);
  }

  function onClickMfaDialogUpdate(event: SyntheticEvent) {
    if (disableDialogButtons) {
      event.preventDefault();
      return;
    }

    setDisableDialogButtons(true);

    // setTimeout causes manage mfa link to open in new tab before this redirect happens
    setTimeout(() => navigateToSettings(true));
  }

  function checkIfDirty(): boolean {
    const newPhone = { phone: getNumericPhoneNumber(customerPhone.phone) };
    return !areCustomersTheSame<CustomerPhone>(initialCustomer, newPhone);
  }

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const field = event.target.id as CustomerPhoneKey;
    const value = event.target.value;
    const updatedPhone = { ...customerPhone, [field]: value };

    setCustomerPhone(updatedPhone);
    setValidationErrors(getValidationErrors(updatedPhone));
  }

  function onBlur(event: FocusEvent<HTMLInputElement>) {
    const field = event.target.id as CustomerPhoneKey;
    setChangedFields(new Set(changedFields).add(field));
  }

  function getValidationErrors(customerPhone: CustomerPhone): ValidationErrors {
    const errors: ValidationErrors = {};

    if (!RegExp(phoneRegExp).test(customerPhone.phone.trim())) {
      errors.phone = "Please provide a valid 10-digit phone number";
    }

    return errors;
  }

  async function onClickSave(): Promise<void> {
    mt.trackAction({ actionType: "button", actionName: "save" });

    setStatus("pending");

    try {
      await saveCustomerInfo({
        ...initialCustomer,
        ...trimCustomerPhone(customerPhone),
      });
    } catch {
      setStatus("mutateError");
      return;
    }

    setStatus("ok");
    if (isMfa) {
      mfaDialog.open();
    } else {
      navigateToSettings(true);
    }
  }

  function trimCustomerPhone(customer: CustomerPhone): CustomerPhone {
    return { phone: customer.phone.trim() };
  }
};
